import { Fragment, useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FormCirculationArea, { CirculationAreaForm } from 'system/components/toponymy/form';
import { CirculationArea } from 'shell/models/circulation-area';
import { CommonToponymyCrumbs } from '../constants';
import { toast } from 'react-toastify';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformResponseCirculationArea } from 'system/utils/transformer-circulation-area';
import { useEventCallback } from '@mui/material';
import { FlagCirculationArea } from 'system/constants/circulation-area-flag';

export type AbilityChangesType = {
  isAbilityChanges: boolean;
  formValues: CirculationAreaForm | undefined;
};

const CirculationAreaEditPage = (): React.ReactElement => {
  const params = useParams();
  const [queryParams] = useSearchParams();

  const [abilityChanges, setAbilityChanges] = useState<AbilityChangesType>({
    isAbilityChanges: false,
    formValues: undefined,
  });

  const civic = queryParams.get('civico') || '';
  const exponent = queryParams.get('esponente') || '';

  const updateCirculationArea = useHttpClient(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${params.id}`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [circulationAreaData] = useHttpFetch<HttpResponse<CirculationArea>, CirculationAreaForm>(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${params.id}`,
    {
      onBootstrap: true,
      options: {
        method: 'GET',
        loading: true,
        errorCatching: true,
      },
      transformer: (response) => transformResponseCirculationArea(response, civic, exponent),
    }
  );

  const navigate = useNavigate();

  const onSubmit = useCallback(
    (formValues: CirculationAreaForm | undefined) => {
      if (!formValues?.streetNumber?.id) {
        toast.warning('Selezionare un numero civico');
        return;
      }
      (
        updateCirculationArea({
          data: {
            motivoCessazione: formValues?.endReason || undefined,
            flag: formValues?.estabilishmentReason || undefined,
            statoCivico: formValues?.civicState || undefined,
            ambitoTerritoriale: formValues?.territorialArea || undefined,
            zonaUrbanistica: formValues?.urbanArea || undefined,
            tipologiaAbitazione: formValues?.civicType || undefined,
            dataCessazione: formValues?.endDateCivic?.toFormat('yyyyMMdd') || undefined,
            cap: formValues?.cap || undefined,
            nuovaDataIstituzione:
              formValues.estabilishmentDateCivic?.toFormat('yyyyMMdd') || undefined,
            key: {
              dataIstituzione: formValues?.dateKey?.toFormat('yyyyMMdd') || undefined,
              esponente: formValues?.key,
            },
            sezCensim: formValues?.censusSection ? Number(formValues?.censusSection) : undefined,
            dsb: formValues?.localIdentity ? Number(formValues?.localIdentity) : undefined,
            municipio: formValues?.municipality.toString() || undefined,
            sezioneElettorale: formValues?.electoralSection.toString() || undefined,
          },
          url: `/civico/${formValues?.streetNumber?.id}`,
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/toponomastica/area-di-circolazione');
      });
    },
    [updateCirculationArea, navigate]
  );

  const onSaveCirculationArea = useEventCallback((formValues: CirculationAreaForm | undefined) => {
    (
      updateCirculationArea({
        data: {
          key: formValues?.keyFromData,
          tipoArea: formValues?.type || undefined,
          flag: formValues?.terminate
            ? FlagCirculationArea.End
            : formValues?.deliberated
            ? FlagCirculationArea.NotDeliberated
            : undefined,
          descrizionePostale: formValues?.name || undefined,
          dataIstituzione: formValues?.estabilishmentDate
            ? formValues.estabilishmentDate.toFormat('yyyyMMdd')
            : undefined,
          dataCessazione: formValues?.endDate ? formValues.endDate.toFormat('yyyyMMdd') : undefined,
          tipoDelibera: formValues?.typeDeliberated || undefined,
          descrizionePostaleRidotta: formValues?.descriptionReducedPostal || undefined,
          descrizioneRidotta: formValues?.descriptionReduced || undefined,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      // disabilitare modifiche
      setAbilityChanges({ formValues: undefined, isAbilityChanges: false });
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonToponymyCrumbs, { name: 'Modifica Area Circolazione' }]} />
      {circulationAreaData.isLoading && !circulationAreaData.data ? null : (
        <FormCirculationArea
          title="Modifica Area Circolazione"
          onOk={onSubmit}
          onCancel={() => {
            navigate('/sistema/toponomastica/area-di-circolazione');
          }}
          initialValues={circulationAreaData.data as CirculationAreaForm}
          circulationArea={Number(params.id)}
          abilityChanges={abilityChanges}
          setAbilityChanges={setAbilityChanges}
          onSaveCirculationArea={onSaveCirculationArea}
        />
      )}
    </Fragment>
  );
};

CirculationAreaEditPage.displayName = 'Circulation Area Edit page component';

export default CirculationAreaEditPage;
